import { Shuffle } from "lucide-react"; // or wherever your Shuffle icon comes from
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select";
import { Filters } from "./index";
import { ScrollArea } from "../../components/ui/ScrollArea";
import { Checkbox } from "../../components/ui/CheckBox";
import { useEffect, useState } from "react";

interface FiltersBarProps {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  handleShuffle: () => void;
  businessProblems: string[];
  companies: string[];
  industries: string[];
}

const FILTER_OPTIONS = {
  difficulty: ["All", "Easy", "Medium", "Hard"],
  company: ["All", "McKinsey & Co", "Bain", "BCG"],
  // We continue to use the same subset of industries as before,
  // matching the enum values exactly (note: they are now lowercase).
  industryFocus: ["All"],
  // Replace the old business problems with the complete set from CaseType.
  businessProblem: ["All"],
  mathFocus: ["All", "High", "Low"],
};

// Shared Option Component
const Option = ({
  label,
  value,
  isSelected,
  isMulti,
  onClick,
}: {
  label: string;
  value: string;
  isSelected: boolean;
  isMulti: boolean;
  onClick: () => void;
}) => (
  <div
    className={`flex items-center gap-2 px-4 py-2 rounded cursor-pointer hover:bg-[var(--color-primary-hover)]  ${
      isSelected ? "bg-[#F1F2F3]" : ""
    }`}
    onClick={onClick}
  >
    {isMulti && (
      <Checkbox
        checked={isSelected}
        className="flex-shrink-0 w-4 h-4 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
      />
    )}
    <span>{label}</span>
  </div>
);

export default function FiltersBar({
  filters,
  setFilters,
  handleShuffle,
  businessProblems,
  companies,
  industries,
}: FiltersBarProps) {
  // Handle single select change
  useEffect(() => {
    setFilterOptions((prev) => ({
      ...prev,
      businessProblem: ["All", ...businessProblems],
      company: ["All", ...companies],
      industryFocus: ["All", ...industries],
    }));
  }, [businessProblems, companies, industries]);

  const [filterOptions, setFilterOptions] = useState(FILTER_OPTIONS);

  const handleSingleSelect = (field: keyof Filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle multi select change
  const handleMultiSelect = (field: keyof Filters, value: string) => {
    setFilters((prev) => {
      const currentValues = prev[field] as string[];

      if (value === "All") {
        return {
          ...prev,
          [field]: [] as string[],
        };
      }

      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [field]: newValues.length === 0 ? [] : newValues,
      };
    });
  };

  // Get display text for multi-select
  const getMultiSelectDisplay = (values: string[]) => {
    if (values.length === 0) return "All";
    return values.join(", ");
  };

  return (
    <div className="flex flex-col w-full mb-8">
      {/* Title row */}
      <div className="flex gap-4">
        {/* <div className="mb-4">
                    <h2 className="text-lg font-semibold">Customization</h2>
                </div> */}

        {/* <div className="flex items-center justify-between mb-4">
                    <button
                        onClick={handleShuffle}
                        className="flex items-center gap-2 border px-3 py-2 rounded text-sm hover:bg-gray-100">
                        <Shuffle className="w-4 h-4" />
                        Shuffle
                    </button>
                </div> */}
      </div>

      {/* Filters container */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Single-select Difficulty */}
        <div className="flex flex-col">
          <label className="mb-1 text-sm font-medium">
            Level of Difficulty
          </label>
          <Select
            value={filters.difficulty}
            onValueChange={(value) => handleSingleSelect("difficulty", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue>{filters.difficulty}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <ScrollArea className="h-[200px]">
                {filterOptions.difficulty.map((option) => (
                  <Option
                    key={option}
                    label={option}
                    value={option}
                    isSelected={filters.difficulty === option}
                    isMulti={false}
                    onClick={() => handleSingleSelect("difficulty", option)}
                  />
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        </div>

        {/* Multi-select Company */}
        <div className="flex flex-col">
          <label className="mb-1 text-sm font-medium">Company Style</label>
          <Select value={getMultiSelectDisplay(filters.company)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All">
                {getMultiSelectDisplay(filters.company)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <ScrollArea className="h-[300px]">
                {filterOptions.company.map((company) => (
                  <Option
                    key={company}
                    label={company}
                    value={company}
                    isSelected={filters.company.includes(company)}
                    isMulti={true}
                    onClick={() => handleMultiSelect("company", company)}
                  />
                ))}
              </ScrollArea>
              <div className="p-2 border text-sm text-gray-500">
                {filters.company.length} / {filterOptions.company.length - 1}
              </div>
            </SelectContent>
          </Select>
        </div>

        {/* Single-select Industry Focus */}
        <div className="flex flex-col">
          <label className="mb-1 text-sm font-medium">Industry Focus</label>
          <Select
            value={filters.industryFocus}
            onValueChange={(value) =>
              handleSingleSelect("industryFocus", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue>{filters.industryFocus}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <ScrollArea className="h-[200px]">
                {filterOptions.industryFocus.map((option) => (
                  <Option
                    key={option}
                    label={option}
                    value={option}
                    isSelected={filters.industryFocus === option}
                    isMulti={false}
                    onClick={() => handleSingleSelect("industryFocus", option)}
                  />
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        </div>

        {/* Multi-select Business Problem */}
        <div className="flex flex-col">
          <label className="mb-1 text-sm font-medium">
            Business Problem Types
          </label>
          <Select value={getMultiSelectDisplay(filters.businessProblem)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All">
                {getMultiSelectDisplay(filters.businessProblem)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <ScrollArea className="h-[300px]">
                {filterOptions.businessProblem.map((problem) => (
                  <Option
                    key={problem}
                    label={problem}
                    value={problem}
                    isSelected={filters.businessProblem.includes(problem)}
                    isMulti={true}
                    onClick={() =>
                      handleMultiSelect("businessProblem", problem)
                    }
                  />
                ))}
              </ScrollArea>
              <div className="p-2 border-t text-sm text-gray-500">
                {filters.businessProblem.length} /{" "}
                {filterOptions.businessProblem.length - 1}
              </div>
            </SelectContent>
          </Select>
        </div>

        {/* Single-select Math Focus */}
        {/* <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium">
                        Math/Quantitative Focus
                    </label>
                    <Select
                        value={filters.mathFocus}
                        onValueChange={(value) =>
                            handleSingleSelect("mathFocus", value)
                        }>
                        <SelectTrigger className="w-full">
                            <SelectValue>{filters.mathFocus}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            <ScrollArea className="h-[200px]">
                                {filterOptions.mathFocus.map((option) => (
                                    <Option
                                        key={option}
                                        label={option}
                                        value={option}
                                        isSelected={
                                            filters.mathFocus === option
                                        }
                                        isMulti={false}
                                        onClick={() =>
                                            handleSingleSelect(
                                                "mathFocus",
                                                option
                                            )
                                        }
                                    />
                                ))}
                            </ScrollArea>
                        </SelectContent>
                    </Select>
                </div> */}
      </div>
    </div>
  );
}
